import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, newOperationId } from '../api';
import { Buscable } from '../Buscable';
import { opcionDeInsumo } from '../busqueda';
import {
  Boton,
  Cabecera,
  Cargando,
  Fallo,
  Pastilla,
  Rotulo,
  Tarjeta,
  Vacio,
} from '../componentes';
import { cantidad, soles } from '../formato';
import { IGV_COMPRAS, costoPorUnidadDeStock, leerCantidadEn, leerImporte, problemaDeCantidad } from '../numeros';
import { FichaDeInsumo } from '../formularios';
import { Escaner } from '../Escaner';
import { CampoDeVencimiento } from '../CampoDeVencimiento';
import { fechaDeCalendario } from '../vencimiento';
import { RecibirOrden } from './RecibirOrden';
import { RecepcionPorSerie } from './RecepcionPorSerie';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * Recibir mercadería.
 *
 * Con orden de compra detrás o sin ella. Con orden, lo recibido se descuenta de
 * lo pedido y la orden queda parcial o recibida: es lo que deja ver que faltan
 * tres kilos antes de que el camión se haya ido. Sin orden también entra,
 * porque media mercadería de un restaurante llega del mercado de la mañana y
 * exigirla convertiría una entrada de dos toques en un trámite.
 *
 * El identificador de operación se genera **al abrir el formulario** y no al
 * enviarlo. Es lo que hace que el doble toque no duplique: dos envíos del mismo
 * formulario llevan el mismo identificador y el servidor devuelve lo que ya
 * hizo en vez de recibir la mercadería dos veces.
 */
export function Recepcion() {
  const vocabulario = useVocabulario();
  const clientes = useQueryClient();
  // Si el negocio exige el vencimiento de cada lote (la botica).
  const ajustesDeLotes = useQuery({
    queryKey: ['ajustes-de-lotes'],
    queryFn: () => almacen.lotSettings(),
    retry: false,
  });
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });

  /*
   * Las órdenes abiertas, para poder recibir **contra** una.
   *
   * Es lo que deja ver que faltan tres kilos antes de que el camión se haya
   * ido. Sin orden también se puede: media mercadería de un restaurante llega
   * del mercado de la mañana, y exigirla convertiría una entrada de dos toques
   * en un trámite.
   */
  const ordenes = useQuery({ queryKey: ['compras'], queryFn: almacen.purchaseOrders });
  const abiertas = (ordenes.data ?? []).filter(
    (orden) =>
      orden.status === 'sent' ||
      orden.status === 'in_transit' ||
      orden.status === 'partial',
  );

  const [operationId, setOperationId] = useState(newOperationId);
  const [supplyId, setSupplyId] = useState('');
  const [cuanto, setCuanto] = useState('');
  const [costo, setCosto] = useState('');
  const [lote, setLote] = useState('');
  const [vence, setVence] = useState('');
  const [guia, setGuia] = useState('');
  const [orderId, setOrderId] = useState('');
  /** Cuántas veces se abrió la misma orden: abrirla otra vez la recarga. */
  const [vueltas, setVueltas] = useState(0);
  const [sinVencimiento, setSinVencimiento] = useState(false);
  /**
   * Llegó en duda —el sello roto, el frasco que no coincide con la guía, el
   * canje del laboratorio sin confirmar—: espera en cuarentena con su lote y
   * su costo, fuera del estante, hasta que quien firma lo libere o lo
   * devuelva (migración 273).
   */
  const [enDuda, setEnDuda] = useState(false);

  /*
   * En qué se recibe y cómo viene el precio.
   *
   * El arroz llega en sacos y se cuenta en kilos; la factura trae el precio con
   * IGV. Teclear «2» sacos a S/ 180 en un insumo que se cuenta en kilos
   * disparaba el costo promedio, y cada quien metía el precio con o sin IGV.
   * Aquí se teclea lo que dice el papel y la cuenta la hace la pantalla: al
   * almacén entra en su unidad y **sin IGV**, que es crédito fiscal y no costo.
   */
  const [enCompra, setEnCompra] = useState(false);
  const [conIgv, setConIgv] = useState(false);

  /** Lo que el escáner leyó y no está en el catálogo. */
  const [noEncontrado, setNoEncontrado] = useState<string | null>(null);

  /*
   * Lo escaneado era una caja o un pack de otro producto que sale de esta
   * ficha: cuántas unidades trae. Entonces se cuentan cajas y se paga por
   * caja, y entra convertido. Antes la recepción no conocía el código de la
   * caja de seis ni el del distribuidor: decía «no existe».
   */
  const [escaneada, setEscaneada] = useState<{
    factor: string;
    codigo: string;
    /** El empaque de la ficha elegido o escaneado: «Rollo 100 m». */
    nombre?: string;
  } | null>(null);

  const elegido = (insumos.data ?? []).find((insumo) => insumo.id === supplyId);
  const pideLote = elegido?.isLotTracked ?? false;

  /*
   * Donde el negocio exige el vencimiento (la botica), el lote es el que trae
   * la caja y no se numera solo: el del día no está impreso en ninguna, y el
   * día que el laboratorio retira un lote nadie sabría si lo tiene. El
   * servidor lo exige igual (`LOT_CODE_REQUIRED`).
   */
  const exigeLoteDelFabricante = pideLote && (ajustesDeLotes.data?.requiresLotExpiry ?? false);
  // Entre todas y no solo las abiertas: la que se acaba de recibir entera pasa
  // a «recibida», y con ella desaparecía la pantalla con lo que entró.
  const ordenElegida = (ordenes.data ?? []).find((orden) => orden.id === orderId);
  const elegibles =
    ordenElegida === undefined || abiertas.some((orden) => orden.id === ordenElegida.id)
      ? abiertas
      : [ordenElegida, ...abiertas];

  /*
   * El último costo, para no inventar el precio: el promedio sin IGV, en la
   * unidad en que se está tecleando. Antes el campo salía vacío con un
   * «38,50» de ejemplo que más de uno dejó puesto.
   */
  const promedio = elegido === undefined ? Number.NaN : Number.parseFloat(elegido.averageCost);
  const ultimoCosto =
    Number.isNaN(promedio) || promedio <= 0
      ? null
      : promedio * (enCompra && elegido?.purchaseFactor != null ? Number.parseFloat(elegido.purchaseFactor) : 1);

  const factor =
    elegido?.purchaseUnit != null && elegido.purchaseFactor != null
      ? Number.parseFloat(elegido.purchaseFactor)
      : null;
  const porUnidadDeCompra = escaneada === null && enCompra && factor !== null && factor > 0;
  const factorEscaneado = escaneada === null ? null : Number.parseFloat(escaneada.factor);

  /** En qué se teclea: el empaque escaneado o la unidad de compra van enteros. */
  const unidadTecleada =
    escaneada !== null ? 'unid' : porUnidadDeCompra ? (elegido?.purchaseUnit ?? null) : (elegido?.unit ?? null);
  const cantidadTecleada = leerCantidadEn(cuanto, unidadTecleada);
  const precioTecleado = leerImporte(costo);

  /** Lo que entra al almacén, en su unidad. */
  const cantidadEnStock = Number.isNaN(cantidadTecleada)
    ? Number.NaN
    : cantidadTecleada * (factorEscaneado ?? (porUnidadDeCompra ? factor! : 1));

  /** El costo por unidad de stock, sin IGV. */
  const costoEnStock = Number.isNaN(precioTecleado)
    ? Number.NaN
    : precioTecleado / (conIgv ? 1 + IGV_COMPRAS / 100 : 1) / (factorEscaneado ?? (porUnidadDeCompra ? factor! : 1));

  /** El mismo costo, como viaja: en enteros y con un solo redondeo. */
  const costoParaEnviar = costoPorUnidadDeStock(costo, {
    conIgv,
    factor: escaneada !== null ? escaneada.factor : porUnidadDeCompra ? elegido!.purchaseFactor : null,
  });

  const recibir = useMutation({
    mutationFn: () =>
      almacen.receive(
        {
          operationId,
          supplyId,
          quantity: cantidadEnStock.toFixed(3),
          unitCost: costoParaEnviar!,
          // Sin el del proveedor, el servidor le pone el del día: L-20260914-01.
          ...(pideLote && lote.trim() !== '' ? { lotCode: lote.trim() } : {}),
          ...(pideLote && vence !== '' ? { expiresAt: vence } : {}),
          ...(guia === '' ? {} : { document: guia }),
          ...(pideLote && enDuda ? { toQuarantine: true } : {}),
        },
        // Un lote que ya llegó vencido se confirma antes de entrar.
        (mensaje) => window.confirm(mensaje),
      ),
    onSuccess: () => {
      // Operación nueva, formulario limpio: lo siguiente que llegue del camión
      // es otra cosa y tiene que poder entrar sin recargar la página.
      setOperationId(newOperationId());
      setCuanto('');
      setCosto('');
      setLote('');
      setVence('');
      setSinVencimiento(false);
      setEnDuda(false);
      void clientes.invalidateQueries();
    },
  });

  /*
   * Lo que va por lote pide su vencimiento, o que alguien diga que no tiene.
   * Un lote sin fecha sale el último en la cola del «primero vence» y nadie
   * avisa cuando se pasa: es el pescado que se pudre al fondo de la cámara.
   */
  const completo =
    supplyId !== '' &&
    cantidadEnStock > 0 &&
    !Number.isNaN(costoEnStock) &&
    costoParaEnviar !== null &&
    (!pideLote || vence !== '' || sinVencimiento) &&
    (!exigeLoteDelFabricante || lote.trim() !== '');

  return (
    <>
      <Cabecera
        titulo="Recepción de mercadería"
        subtitulo="Lo que llega al local, con su lote y su costo" ayuda="Aquí entra lo que trae el proveedor: qué, cuánto, a qué precio y, si caduca, su lote y vencimiento. Es lo que alimenta el stock y el costo."
        acciones={
          /*
           * Escanear en la recepción elige el insumo, no suma: lo que llega
           * viene en cajas de veinticuatro y el número lo dice el albarán, no
           * el conteo de envases uno por uno.
           */
          <Escaner
            onCodigo={async (codigo) => {
              const limpio = codigo.trim().toLowerCase();
              const local = (insumos.data ?? []).find(
                (uno) =>
                  (uno.barcode ?? '').toLowerCase() === limpio ||
                  uno.sku.toLowerCase() === limpio,
              );

              // Lo que no es el código de la ficha se le pregunta al servidor:
              // el de la caja, el del pack, el alterno del distribuidor.
              const remoto =
                local !== undefined ? null : await almacen.receivableByCode(codigo.trim()).catch(() => null);
              const encontrado =
                local ?? (remoto === null ? undefined : (insumos.data ?? []).find((uno) => uno.id === remoto.supplyId));

              /*
               * Un código que no existe se dice, y el insumo anterior se
               * suelta. Antes se ignoraba en silencio con el de antes todavía
               * elegido, y la caja escaneada entraba como el producto anterior.
               */
              if (encontrado === undefined) {
                setSupplyId('');
                setEscaneada(null);
                setNoEncontrado(codigo.trim());
                return;
              }

              setNoEncontrado(null);
              setEnCompra(false);
              const empaque = encontrado.packages?.find((uno) => uno.barcode === codigo.trim());
              setEscaneada(
                remoto?.unitsPerScan == null
                  ? null
                  : {
                      factor: remoto.unitsPerScan,
                      codigo: codigo.trim(),
                      ...(empaque === undefined ? {} : { nombre: empaque.name }),
                    },
              );
              setSupplyId(encontrado.id);
              // Lo siguiente es teclear cuánto llegó: el foco va ahí.
              window.setTimeout(() => document.getElementById('cuanto')?.focus(), 0);
            }}
            etiqueta="Escanear lo que llegó"
          />
        }
      />

      <div className="cuerpo">
        <div className={ordenElegida === undefined ? 'dos-columnas' : undefined}>
          <Tarjeta>
            <h2 className="tarjeta__titulo">Ingreso</h2>

            {insumos.isLoading ? (
              <Cargando que="el catálogo" />
            ) : (insumos.data ?? []).length === 0 ? (
              /*
               * Un local recién instalado abría esta pantalla y veía un
               * formulario con el desplegable vacío y ningún motivo. Recibir
               * mercadería que no está en el catálogo no es posible, y eso hay
               * que decirlo aquí, no dejarlo adivinar.
               */
              <Vacio
                titulo={`No hay ${vocabulario.insumos} que recibir`}
                mensaje={`La entrada se registra contra un ${vocabulario.insumo} del catálogo: hace falta darlo de alta antes, con su unidad y su categoría. Es una vez por ${vocabulario.insumo}, no por entrega.`}
                accion={<FichaDeInsumo />}
              />
            ) : (
              <div className="formulario">
                {/*
                 * La orden, lo primero. Estaba al final y elegirla no cambiaba
                 * nada: se volvían a teclear cantidades y precios que la orden ya
                 * sabía. Ahora trae sus líneas pendientes, listas para recibir.
                 */}
                {elegibles.length === 0 ? null : (
                  <div className="campo">
                    <Rotulo htmlFor="orden" ayuda="Si esto se pidió con una orden de compra, elígela: salen sus líneas pendientes con la cantidad y el precio pactado, y se reciben de una vez.">Contra una orden</Rotulo>
                    <Buscable
                      id="orden"
                      valor={orderId}
                      onCambio={setOrderId}
                      sinEleccion="Sin orden: compra al paso, mercado"
                      placeholder="Busca por número o proveedor…"
                      opciones={elegibles.map((orden) => ({
                        valor: orden.id,
                        etiqueta: `${orden.number} · ${orden.supplier}`,
                        detalle: `${orden.lines.filter((l) => Number.parseFloat(l.pending) > 0).length} por llegar${
                          orden.expectedAt === null
                            ? ''
                            : ` · se esperaba el ${fechaDeCalendario(orden.expectedAt.slice(0, 10))}`
                        }`,
                      }))}
                    />
                  </div>
                )}

                {ordenElegida !== undefined ? (
                  <RecibirOrden
                    key={`${ordenElegida.id}-${vueltas}`}
                    orden={ordenElegida}
                    insumos={insumos.data ?? []}
                    onOtraVez={() => setVueltas((antes) => antes + 1)}
                  />
                ) : (
                <>
                {noEncontrado === null ? null : (
                  <p className="aviso-caja aviso-caja--peligro" role="alert">
                    Ningún {vocabulario.insumo} tiene el código <span className="mono">{noEncontrado}</span>.
                    Elígelo a mano o dale ese código de barras en su ficha.
                  </p>
                )}

                <div className="campo">
                  <Rotulo htmlFor="que" ayuda="Lo que llegó. También se puede escanear el código de barras del envase.">{mayuscula(vocabulario.insumo)}</Rotulo>
                  <Buscable
                    id="que"
                    valor={supplyId}
                    onCambio={(insumoId) => {
                      setSupplyId(insumoId);
                      setNoEncontrado(null);
                      setEnCompra(false);
                      setEscaneada(null);
                    }}
                    placeholder={`Busca el ${vocabulario.insumo} por nombre o código…`}
                    opciones={(insumos.data ?? []).map((insumo) =>
                      opcionDeInsumo(insumo, `quedan ${cantidad(insumo.quantity)} ${insumo.unit}`),
                    )}
                  />
                </div>

                {escaneada === null || escaneada.nombre !== undefined || elegido === undefined ? null : (
                  <p className="aviso-caja">
                    Escaneaste un empaque que trae <strong>{cantidad(escaneada.factor)} {elegido.unit}</strong>{' '}
                    (<span className="mono">{escaneada.codigo}</span>): escribe cuántos llegaron y su precio
                    por empaque, y entra convertido.{' '}
                    <button type="button" className="enlace-sutil" onClick={() => setEscaneada(null)}>
                      Contar por {elegido.unit}
                    </button>
                  </p>
                )}

                {/*
                 * Lo que va por serie se recibe equipo por equipo: una cantidad
                 * no dice qué celulares llegaron. Docs/15 §2.41.
                 */}
                {elegido?.isSerialTracked === true ? (
                  <>
                    <div className="campo">
                      <Rotulo htmlFor="guia" ayuda="El número de la guía o factura del proveedor, para encontrarla después si algo no cuadra.">Guía de remisión</Rotulo>
                      <input
                        id="guia"
                        placeholder="GR-44821"
                        value={guia}
                        onChange={(e) => setGuia(e.target.value)}
                      />
                    </div>
                    <RecepcionPorSerie key={elegido.id} insumo={elegido} guia={guia} />
                  </>
                ) : (
                <>
                {/*
                 * En qué llegó: la unidad del insumo, su unidad de compra y cada
                 * empaque de la ficha —el rollo de 50 m y el de 100 m—
                 * (migración 276). Elegido el empaque, se cuentan rollos y se
                 * paga por rollo, y entra convertido.
                 */}
                {elegido !== undefined && (factor !== null || (elegido.packages ?? []).length > 0) ? (
                  <div className="opciones" role="group" aria-label="Se recibe en">
                    <button
                      type="button"
                      className={`opcion ${!enCompra && escaneada === null ? 'opcion--activa' : ''}`}
                      aria-pressed={!enCompra && escaneada === null}
                      onClick={() => {
                        setEnCompra(false);
                        setEscaneada(null);
                      }}
                    >
                      En {elegido.unit}
                    </button>
                    {factor === null ? null : (
                      <button
                        type="button"
                        className={`opcion ${enCompra && escaneada === null ? 'opcion--activa' : ''}`}
                        aria-pressed={enCompra && escaneada === null}
                        onClick={() => {
                          setEnCompra(true);
                          setEscaneada(null);
                        }}
                      >
                        En {elegido.purchaseUnit} ({cantidad(elegido.purchaseFactor ?? '')} {elegido.unit})
                      </button>
                    )}
                    {(elegido.packages ?? []).map((empaque) => (
                      <button
                        key={empaque.id}
                        type="button"
                        className={`opcion ${escaneada?.nombre === empaque.name ? 'opcion--activa' : ''}`}
                        aria-pressed={escaneada?.nombre === empaque.name}
                        onClick={() => {
                          setEnCompra(false);
                          setEscaneada({ factor: empaque.units, codigo: empaque.barcode ?? '', nombre: empaque.name });
                        }}
                      >
                        {empaque.name} ({cantidad(empaque.units)} {elegido.unit})
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="formulario__fila">
                  <div className="campo">
                    <Rotulo htmlFor="cuanto" ayuda={`Cuánto llegó. Si el ${vocabulario.insumo} tiene unidad de compra, puedes contar en sacos o cajas y entra convertido.`}>
                      Cantidad{' '}
                      {elegido === undefined
                        ? ''
                        : escaneada !== null
                          ? escaneada.nombre !== undefined
                            ? `(${escaneada.nombre})`
                            : `(empaques de ${cantidad(escaneada.factor)})`
                          : `(${porUnidadDeCompra ? elegido.purchaseUnit : elegido.unit})`}
                    </Rotulo>
                    <input
                      id="cuanto"
                      inputMode="decimal"
                      placeholder="9,06"
                      value={cuanto}
                      onChange={(e) => setCuanto(e.target.value)}
                    />
                    {problemaDeCantidad(cuanto, unidadTecleada) === null ? null : (
                      <span className="negativo">{problemaDeCantidad(cuanto, unidadTecleada)}</span>
                    )}
                  </div>

                  <div className="campo">
                    <Rotulo htmlFor="costo" ayuda="Lo que se pagó por cada unidad. Si el precio de la factura trae IGV, márcalo abajo y se lo quita: al almacén entra sin IGV.">
                      Precio por{' '}
                      {escaneada !== null ? 'empaque' : porUnidadDeCompra ? elegido?.purchaseUnit : (elegido?.unit ?? 'unidad')}
                    </Rotulo>
                    <input
                      id="costo"
                      inputMode="decimal"
                      placeholder="38,50"
                      value={costo}
                      onChange={(e) => setCosto(e.target.value)}
                    />
                    {ultimoCosto === null ? null : (
                      <button
                        type="button"
                        className="enlace-sutil"
                        onClick={() => {
                          setCosto(ultimoCosto.toFixed(2).replace('.', ','));
                          setConIgv(false);
                        }}
                      >
                        Último costo: {soles(ultimoCosto)} sin IGV · usar
                      </button>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  className={`opcion ${conIgv ? 'opcion--activa' : ''}`}
                  aria-pressed={conIgv}
                  onClick={() => setConIgv(!conIgv)}
                >
                  El precio incluye IGV ({IGV_COMPRAS} %)
                  <Pastilla
                    texto={conIgv ? 'se le quita al entrar' : 'ya va sin IGV'}
                    tono={conIgv ? 'aviso' : 'neutro'}
                  />
                </button>

                {pideLote ? (
                  <div className="formulario__fila">
                    <div className="campo">
                      {exigeLoteDelFabricante ? (
                        <Rotulo htmlFor="lote" ayuda="El número de lote impreso en la caja. Aquí es obligatorio: es el que se busca el día que el fabricante retira un lote, para saber si lo tienes y a quién se vendió.">Lote del fabricante</Rotulo>
                      ) : (
                        <Rotulo htmlFor="lote" ayuda="El número de lote que trae el envase, si lo tiene: es el que te pedirá el proveedor si hay un reclamo. Si lo dejas vacío, se numera solo.">Lote del proveedor (opcional)</Rotulo>
                      )}
                      <input
                        id="lote"
                        placeholder={exigeLoteDelFabricante ? 'El que trae la caja' : 'Se pone solo'}
                        required={exigeLoteDelFabricante}
                        value={lote}
                        onChange={(e) => setLote(e.target.value)}
                      />
                      <p className="nota-sm nota--pegada">
                        {exigeLoteDelFabricante
                          ? 'Cópialo de la caja tal cual. Sin él no entra: el día de un retiro es lo único que dice si lo tienes y a quién lo vendiste.'
                          : 'Si el envase trae su número de lote, anótalo: es el que pide el proveedor el día de un reclamo. Si no, se numera solo.'}
                      </p>
                    </div>

                  </div>
                ) : null}

                {pideLote ? (
                  <div className="campo">
                    <Rotulo htmlFor="vence" ayuda="La fecha de vencimiento del envase. Lo que vence antes sale primero, y el sistema avisa antes de que se pase.">Vence</Rotulo>
                    <CampoDeVencimiento
                      id="vence"
                      valor={vence}
                      onCambio={setVence}
                      sinFecha={sinVencimiento}
                      onSinFecha={setSinVencimiento}
                      vidaUtilDias={elegido?.shelfLifeDays ?? null}
                      permiteSinFecha={
                        !(ajustesDeLotes.data?.requiresLotExpiry ?? false) &&
                        !(elegido?.requiresExpiry ?? false)
                      }
                      margenDias={ajustesDeLotes.data?.expiryAlertDays ?? 3}
                    />
                  </div>
                ) : null}

                {pideLote ? (
                  <label className="campo campo--en-linea">
                    <input
                      type="checkbox"
                      checked={enDuda}
                      onChange={(evento) => setEnDuda(evento.target.checked)}
                    />
                    <span>
                      Llegó en duda: a cuarentena
                      <span className="nota-sm nota--pegada">
                        {' '}
                        Sello roto, no coincide con la guía o es un canje sin confirmar. No entra
                        al estante hasta que se libere en Cuarentena.
                      </span>
                    </span>
                  </label>
                ) : null}

                <div className="campo">
                  <Rotulo htmlFor="guia" ayuda="El número de la guía o factura del proveedor, para encontrarla después si algo no cuadra.">Guía de remisión</Rotulo>
                  <input
                    id="guia"
                    placeholder="GR-44821"
                    value={guia}
                    onChange={(e) => setGuia(e.target.value)}
                  />
                </div>

                {recibir.isError ? <Fallo error={recibir.error} /> : null}

                <Boton
                  variante="primario"
                  onClick={() => recibir.mutate()}
                  disabled={!completo || recibir.isPending}
                >
                  {recibir.isPending ? 'Ingresando…' : 'Confirmar ingreso'}
                </Boton>
                </>
                )}
                </>
                )}
              </div>
            )}
          </Tarjeta>

          {ordenElegida !== undefined ? null : (
          <Tarjeta>
            <h2 className="tarjeta__titulo">Qué va a pasar</h2>

            {elegido === undefined ? (
              <p className="nota nota--pegada">
                Elige un {vocabulario.insumo} y aquí se ve qué entra, cuánto queda después y si
                hace falta el lote.
              </p>
            ) : (
              <dl className="resumen-pares">
                <div>
                  <dt>{mayuscula(vocabulario.insumo)}</dt>
                  <dd>{elegido.name}</dd>
                </div>
                <div>
                  <dt>Tiene ahora</dt>
                  <dd className="mono">
                    {cantidad(elegido.quantity)} {elegido.unit}
                  </dd>
                </div>
                <div>
                  <dt>Entra</dt>
                  <dd className="mono">
                    {Number.isNaN(cantidadEnStock)
                      ? '—'
                      : `${cantidad(cantidadEnStock.toFixed(3))} ${elegido.unit}`}
                  </dd>
                </div>
                <div>
                  <dt>Quedará</dt>
                  <dd className="mono">
                    {Number.isNaN(cantidadEnStock)
                      ? '—'
                      : `${cantidad(
                          (Number.parseFloat(elegido.quantity) + cantidadEnStock).toFixed(3),
                        )} ${elegido.unit}`}
                  </dd>
                </div>
                <div>
                  <dt>Costo por {elegido.unit}, sin IGV</dt>
                  <dd className="mono">
                    {Number.isNaN(costoEnStock) ? '—' : soles(costoEnStock)}
                  </dd>
                </div>
                <div>
                  <dt>Total a ingresar, sin IGV</dt>
                  <dd className="mono">
                    {Number.isNaN(cantidadEnStock) || Number.isNaN(costoEnStock)
                      ? '—'
                      : soles(cantidadEnStock * costoEnStock)}
                  </dd>
                </div>
                <div>
                  <dt>Control por lote</dt>
                  <dd>
                    <Pastilla
                      texto={pideLote ? 'Obligatorio' : 'A granel'}
                      tono={pideLote ? 'aviso' : 'neutro'}
                    />
                  </dd>
                </div>
              </dl>
            )}

            {recibir.isSuccess && recibir.data.quarantined === true ? (
              <p className="aviso-caja" style={{ marginTop: 'var(--hueco-lg)' }}>
                En cuarentena
                {recibir.data.lot == null ? '' : (
                  <>
                    {' '}con el lote <span className="mono">{recibir.data.lot.code}</span>
                  </>
                )}
                . No entró al estante ni al costo: se libera o se devuelve desde Cuarentena.
              </p>
            ) : recibir.isSuccess ? (
              <p className="aviso-caja" style={{ marginTop: 'var(--hueco-lg)' }}>
                Ingresado. El saldo quedó en{' '}
                <span className="mono">{cantidad(recibir.data.balance)}</span> y el
                movimiento ya está en el kardex con tu nombre.
                {recibir.data.lot == null ? null : (
                  <>
                    {' '}Entró en el lote <span className="mono">{recibir.data.lot.code}</span>
                    {recibir.data.lot.expiresAt === null
                      ? ', sin fecha de vencimiento.'
                      : `, vence el ${fechaDeCalendario(recibir.data.lot.expiresAt)}.`}
                    {recibir.data.lot.addedToExisting
                      ? ' Ese lote ya existía: se sumó a él y se quedó con su fecha.'
                      : ''}
                  </>
                )}
              </p>
            ) : null}
          </Tarjeta>
          )}
        </div>
      </div>
    </>
  );
}
